import React, {
  useState,
  useRef,
  useCallback,
  useContext,
  useMemo,
  useEffect,
} from "react";
import { UserContext } from "context";
import {
  Box,
  Menu,
  Button,
  MenuItem,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as DialogButton,
  Typography,
} from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { CreatePolicyDialog } from "./CreatePolicyDialog";
import RetirePolicyDialog from "./RetirePolicyDialog";
import CreatePolicyUpdateDialog from "./CreatePolicyUpdateDialog";
import { hasSkill } from "utils/shared";
import Api from "api";
import { ACTIONS } from "./Constants";
import { Paragliding } from "@mui/icons-material";
import { MESSAGES } from "./Constants";
import { postUserActivity } from "components/shared/postUserActivity";
import { WORKFLOW_BULK_UPDATE } from "constants/Constants";

const BulkUpdateSelect = ({
  selectedPolicies,
  allPolicies,
  selectedHealthPlan,
  refreshData,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const dialogTitle = useRef("");
  const dialogMessage = useRef("");
  const dialogType = useRef("success");

  const [createPolicyDialogOpen, setCreatePolicyDialogOpen] = useState(false);
  const [retirePolicyDialogOpen, setRetirePolicyDialogOpen] = useState(false);
  const [policyUpdateDialogOpen, setPolicyUpdateDialogOpen] = useState(false);
  const [effectiveSelectedPolicies, setEffectiveSelectedPolicies] =
    useState(selectedPolicies);
  const [latestVersionDialogOpen, setLatestVersionDialogOpen] = useState(false);
  const [latestSelectedPolicies, setLatestSelectedPolicies] = useState([]);
  const [pendingLatestPolicies, setPendingLatestPolicies] = useState([]);
  const [pendingAnchorEl, setPendingAnchorEl] = useState(null);

  const open = Boolean(anchorEl);
  const hasLatestSelectedPolicies = latestSelectedPolicies.length > 0;
  const userContext = useContext(UserContext);

  useEffect(() => {
    setEffectiveSelectedPolicies(selectedPolicies);
  }, [selectedPolicies]);

  const getPolicyFamilyKey = (policy) =>
    policy?.parentPolicyKey != null
      ? policy.parentPolicyKey
      : policy?.policyKey;

  const getComparableVersionParts = (version) => {
    const normalizedVersion = String(version || "").trim();
    const baseVersion = normalizedVersion.split("-")[0];
    const firstDecimalVersion = baseVersion.match(/^\d+(?:\.\d+)?/)?.[0] || "0";

    return firstDecimalVersion
      .split(".")
      .map((part) => Number.parseInt(part, 10) || 0);
  };

  const compareVersions = (versionA, versionB) => {
    const partsA = getComparableVersionParts(versionA);
    const partsB = getComparableVersionParts(versionB);
    const maxLen = Math.max(partsA.length, partsB.length);

    for (let i = 0; i < maxLen; i += 1) {
      const a = partsA[i] || 0;
      const b = partsB[i] || 0;
      if (a > b) return 1;
      if (a < b) return -1;
    }
    return 0;
  };

  const latestPolicyByFamily = useMemo(() => {
    if (!Array.isArray(allPolicies)) return new Map();

    return allPolicies.reduce((latestMap, policy) => {
      const familyKey = getPolicyFamilyKey(policy);
      const currentLatest = latestMap.get(familyKey);

      if (
        !currentLatest ||
        compareVersions(policy?.version, currentLatest?.version) > 0
      ) {
        latestMap.set(familyKey, policy);
      }

      return latestMap;
    }, new Map());
  }, [allPolicies]);

  const handleClick = useCallback(
    (event) => {
      const nonLatest = selectedPolicies.filter((policy) => {
        const latestPolicy = latestPolicyByFamily.get(
          getPolicyFamilyKey(policy)
        );
        return latestPolicy && latestPolicy.policyKey !== policy.policyKey;
      });

      if (nonLatest.length === 0) {
        setEffectiveSelectedPolicies(selectedPolicies);
        setAnchorEl(event.currentTarget);
        return;
      }

      const latestPolicies = selectedPolicies.filter((policy) => {
        const latestPolicy = latestPolicyByFamily.get(
          getPolicyFamilyKey(policy)
        );
        return latestPolicy && latestPolicy.policyKey === policy.policyKey;
      });

      setPendingAnchorEl(event.currentTarget);
      setPendingLatestPolicies(latestPolicies);
      setLatestSelectedPolicies(latestPolicies);
      setLatestVersionDialogOpen(true);
    },
    [latestPolicyByFamily, selectedPolicies]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleLatestVersionContinue = useCallback(() => {
    setEffectiveSelectedPolicies(pendingLatestPolicies);
    setLatestVersionDialogOpen(false);
    if (pendingAnchorEl) {
      setAnchorEl(pendingAnchorEl);
    }
    setPendingAnchorEl(null);
    setPendingLatestPolicies([]);
    setLatestSelectedPolicies([]);
  }, [pendingAnchorEl, pendingLatestPolicies]);

  const handleLatestVersionCancel = useCallback(() => {
    setLatestVersionDialogOpen(false);
    setPendingAnchorEl(null);
    setPendingLatestPolicies([]);
    setLatestSelectedPolicies([]);
  }, []);

  const handleCreatePolicyDialogClose = useCallback(() => {
    setCreatePolicyDialogOpen(false);
  }, []);

  const handleRetirePolicyDialogClose = useCallback(() => {
    setRetirePolicyDialogOpen(false);
  }, []);

  const handlePolicyUpdateDialogClose = useCallback(() => {
    setPolicyUpdateDialogOpen(false);
  }, []);

  const formatResponseData = (data) => {
    return Object.entries(data)
      .map(([key, value]) => `${key}: ${value}`)
      .join("\n");
  };

  const showDialog = (title, message, type = "success") => {
    dialogTitle.current = title;
    dialogMessage.current = message;
    dialogType.current = type;
    setDialogOpen(true);
  };

  const closeBulkActionDialog = (action) => {
    const actionHandlers = {
      [ACTIONS.CREATE_POLICY]: () => setCreatePolicyDialogOpen(false),
      [ACTIONS.UPDATE_POLICY]: () => setPolicyUpdateDialogOpen(false),
      [ACTIONS.RETIRE_POLICY]: () => setRetirePolicyDialogOpen(false),
    };

    if (action) {
      const handler = actionHandlers[action];
      if (handler) {
        handler();
      }
    } else {
      console.log("Bulk Action is not passed, there is nothing to close");
    }
  };

  const handleGenericBulkActionConfirm = async ({
    action,
    selectedHealthPlans,
    cabQuarter,
    cabYear,
    categoryOfWork,
    effectiveDate,
  }) => {
    setLoading(true);
    const policyKeys = effectiveSelectedPolicies.map(
      (policy) => policy.policyKey
    );
    const requestData = {
      selectedPolicies: policyKeys,
      selectedHPs: selectedHealthPlans,
      cabQuarter: cabQuarter,
      cabYear: cabYear,
      workCategoryKey: categoryOfWork,
      effectiveDate: effectiveDate,
      action: action,
    };

    const numberOfRequests = requestData.selectedPolicies.length;

    try {
      const response = await Api.policyEnforcementGrid.genericBulkAction(
        selectedHealthPlan.healthPlanKey,
        requestData
      );
      if (response.status === 200) {
        postUserActivity(
          selectedHealthPlan.healthPlanKey,
          null,
          response.status,
          WORKFLOW_BULK_UPDATE,
          "Click",
          "BulkUpdateSelect",
          action,
          "Bulk Update"
        );
        showDialog(action + " successful", response.data, "success");
        closeBulkActionDialog(action);
      } else {
        let errorResponse = Object.values(
          response?.data?.errors || response?.message
        );
        const numberOfErrors = errorResponse.length;
        errorResponse.push(
          `Error: ${numberOfErrors} of ${numberOfRequests} requests failed`
        );
        showDialog("Validation Error " + action, errorResponse);
      }
    } catch (error) {
      let errorResponse = null;
      let numberOfErrors = 0;

      if (error.response?.data?.data) {
        errorResponse = Object.values(error.response.data.data);
      } else if (error.response?.data?.errors) {
        errorResponse = Object.values(error.response.data.errors);
      }

      if (errorResponse) {
        try {
          numberOfErrors = errorResponse.length;
          errorResponse.push(
            `Error: ${numberOfErrors} of ${numberOfRequests} requests failed`
          );
        } catch (parseError) {
          console.error("Failed to parse error response:", parseError);
        }
      }

      showDialog(
        "Validation Error",
        errorResponse ||
          `${error.code}: ${error.message} error: ${numberOfErrors} of ${numberOfRequests} requests failed`
      );
    } finally {
      setLoading(false);
    }
  };

  const policyDraftedClick = () => {
    const failedValidations = policyDraftedValidation();
    if (failedValidations?.length > 0) {
      showDialog("Validation Errors", failedValidations, "error");
    } else {
      handleGenericBulkActionConfirm({ action: ACTIONS.POLICY_DRAFTED });
    }
    handleClose();
  };

  const policyDraftedValidation = () => {
    const failedValidations = [];

    effectiveSelectedPolicies.forEach((policy) => {
      if (policy) {
        const { number, pdc } = policy;
        if (!pdc || !pdc.status || pdc.status !== "Current Quarter") {
          failedValidations.push({
            number,
            error:
              "Policy must be identified for the current quarter in order to be drafted",
          });
        }
      }
    });

    return failedValidations;
  };

  const createPoliciesClick = () => {
    if (selectedHealthPlan?.healthPlanName === "AHCS") {
      const failedValidations = createPoliciesValidation();
      if (failedValidations?.length > 0) {
        showDialog("Validation Errors", failedValidations, "error");
      } else {
        setCreatePolicyDialogOpen(true);
      }
    } else {
      showDialog(
        "Error",
        "This action is only allowed for Avalon Policies.",
        "error"
      );
    }
    handleClose();
  };

  const createPoliciesValidation = () => {
    const failedValidations = [];

    effectiveSelectedPolicies.forEach((policy) => {
      if (policy) {
        const { number, pdc, coding, translation, bulkActionWorkflowRefDTO } =
          policy;
        const customerReviewName = bulkActionWorkflowRefDTO?.customerReviewName;
        const categoryOfWork = bulkActionWorkflowRefDTO?.categoryOfWorkName;
        // Validation 1: If customerReviewName is "SmartSheet" and pdc status is "Complete"
        if (categoryOfWork === "CAB" && pdc?.status !== "CAB Approved") {
          failedValidations.push({
            number,
            error: "CAB Approval required to create Health Plan policies",
          });
        } else if (
          (!customerReviewName || customerReviewName === "No") &&
          (coding?.status !== "Complete" || translation?.status !== "Complete")
        ) {
          failedValidations.push({
            number,
            error:
              "Coding & Translation must be completed before creating Health Plan policies",
          });
        }
      }
    });

    return failedValidations;
  };

  const createPolicyUpdateClick = () => {
    setPolicyUpdateDialogOpen(true);
    handleClose();
  };

  const retirePolicyClick = () => {
    setRetirePolicyDialogOpen(true);
    handleClose();
  };

  const cabApprovedClick = () => {
    const failedValidations = cabApprovedValidation(ACTIONS.CAB_APPROVED);
    if (failedValidations?.length > 0) {
      showDialog("Validation Errors", failedValidations, "error");
    } else {
      handleGenericBulkActionConfirm({ action: ACTIONS.CAB_APPROVED });
    }
    handleClose();
  };

  const cabApprovedValidation = (updateString) => {
    const failedValidations = [];

    effectiveSelectedPolicies.forEach((policy) => {
      if (policy) {
        const { number, coding, translation } = policy;

        if (
          coding?.status !== "Complete" ||
          translation?.status !== "Complete"
        ) {
          failedValidations.push({
            number,
            error: MESSAGES.CAB_VALIDATION_ERR_MSG + updateString,
          });
        }
      }
    });

    return failedValidations;
  };

  const postCabUpdateClick = () => {
    const failedValidations = cabApprovedValidation(ACTIONS.POST_CAB_UPDATE);
    if (failedValidations?.length > 0) {
      showDialog("Validation Errors", failedValidations, "error");
    } else {
      handleGenericBulkActionConfirm({ action: ACTIONS.POST_CAB_UPDATE });
    }
    handleClose();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    if (dialogType.current === "success") {
      refreshData(); // Refresh data only for success messages
    }
  };

  return (
    <Box sx={{ minWidth: 120 }}>
      <Button
        id="bulk-Updates-Button"
        aria-controls={open ? "bulk-Updates-Select" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        endIcon={
          open ? (
            <ArrowDropUpIcon sx={{ ml: -1 }} />
          ) : (
            <ArrowDropDownIcon sx={{ ml: -1 }} />
          )
        }
        disabled={selectedPolicies.length === 0}
      >
        Bulk Updates
      </Button>
      <Menu
        id="bulk-Updates-Select"
        aria-labelledby="bulk-Updates-Button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {hasSkill(userContext, ["Manager", "Policy"]) && (
          <MenuItem onClick={createPolicyUpdateClick}>
            Create Policy Update
          </MenuItem>
        )}
        {hasSkill(userContext, ["Manager"]) && (
          <MenuItem onClick={policyDraftedClick}>Policy Drafted</MenuItem>
        )}
        {hasSkill(userContext, ["Manager"]) && (
          <MenuItem onClick={cabApprovedClick}>CAB Approved</MenuItem>
        )}

        {hasSkill(userContext, ["Coding", "Manager"]) && (
          <MenuItem onClick={postCabUpdateClick}>Post-CAB Update</MenuItem>
        )}

        {hasSkill(userContext, ["Manager", "Policy"]) &&
          selectedHealthPlan?.healthPlanName === "AHCS" && (
            <MenuItem onClick={createPoliciesClick}>
              Create Health Plan Policies
            </MenuItem>
          )}
        {hasSkill(userContext, ["Manager", "Policy"]) && (
          <MenuItem onClick={retirePolicyClick}>Retire Policy</MenuItem>
        )}
      </Menu>

      <Dialog
        open={latestVersionDialogOpen}
        onClose={handleLatestVersionCancel}
        maxWidth="sm"
      >
        <DialogTitle>
          <strong>Confirm Latest Version Action</strong>
        </DialogTitle>
        <DialogContent>
          {hasLatestSelectedPolicies ? (
            <>
              {latestSelectedPolicies.map((policy) => (
                <Typography key={policy.policyKey} sx={{ mb: 1 }}>
                  {`${policy.title} ${policy.version}`}
                </Typography>
              ))}
              <Typography>
                Action will be taken on latest version. Do you want to continue?
              </Typography>
            </>
          ) : (
            <Typography>No latest version policies selected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <DialogButton onClick={handleLatestVersionCancel}>
            Cancel
          </DialogButton>
          <DialogButton
            disabled={!hasLatestSelectedPolicies}
            onClick={handleLatestVersionContinue}
            autoFocus
          >
            Continue
          </DialogButton>
        </DialogActions>
      </Dialog>

      {/* Create Policy Update Dialog */}
      {policyUpdateDialogOpen && (
        <CreatePolicyUpdateDialog
          open={policyUpdateDialogOpen}
          handleClose={handlePolicyUpdateDialogClose}
          handleConfirm={handleGenericBulkActionConfirm}
        />
      )}

      {/* Create Policy Dialog */}
      {createPolicyDialogOpen && (
        <CreatePolicyDialog
          open={createPolicyDialogOpen}
          handleClose={handleCreatePolicyDialogClose}
          handleConfirm={handleGenericBulkActionConfirm}
        />
      )}

      {/* Retire Policy Dialog */}
      {retirePolicyDialogOpen && (
        <RetirePolicyDialog
          open={retirePolicyDialogOpen}
          selectedHealthPlan={selectedHealthPlan}
          handleClose={handleRetirePolicyDialogClose}
          handleConfirm={handleGenericBulkActionConfirm}
        />
      )}
      {/* Loading Mask */}
      <Backdrop open={loading} style={{ zIndex: 1000, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {/* Success/Error Dialog */}
      {dialogOpen && (
        <Dialog
          maxWidth="md"
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
        >
          <DialogTitle>
            <strong>{dialogTitle.current}</strong>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" component="div">
              {Array.isArray(dialogMessage.current)
                ? dialogMessage.current.map((line, index) => (
                    <div key={index}>
                      {typeof line === "object" && line !== null ? (
                        <>
                          <strong>{line.number}</strong> : {line.error}
                        </>
                      ) : (
                        line
                      )}
                    </div>
                  ))
                : typeof dialogMessage.current === "object" &&
                    !Array.isArray(dialogMessage.current)
                  ? Object.entries(dialogMessage.current).map(
                      ([key, value], index) => (
                        <div key={index}>
                          <strong>{key}</strong>: {String(value)}
                        </div>
                      )
                    )
                  : dialogMessage.current
                      .split("\n")
                      .map((line, index) => <div key={index}>{line}</div>)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <DialogButton onClick={handleDialogClose}>OK</DialogButton>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default BulkUpdateSelect;
